import { useState, useEffect } from 'react';
import DatePicker, { registerLocale } from "react-datepicker";
import { zhCN, enGB, enUS } from "date-fns/locale";

import useLocale from '../Utilities/useLocale';
import { convertUTCToLocalTime, convertLocalTimeZoneToUTC, convertToDateTime, useDateUtils } from '../Utilities/useDateUtils';
import "react-datepicker/dist/react-datepicker.css";
import "./DateTimePicker.css";
import { DateTime } from 'luxon';

export interface DateTimePickerProps {
    name: string,
    dateTimeUTC: DateTime | null,
    handleDateSelect: (dateUTC: DateTime | null) => void,
    dateOnly?: boolean,
    isClearable?: boolean,
    required?: boolean,
    isInvalid?: boolean,
    showIcon?: boolean,
    inline?: boolean,
    onClickOutside?: () => void,
}

const DateTimePicker = ({ name, dateTimeUTC, handleDateSelect, isClearable = false, dateOnly = false, required = true, isInvalid = false, showIcon = true, inline = false, onClickOutside }: DateTimePickerProps) => {
    registerLocale('zh-CN', zhCN);
    registerLocale('en-GB', enGB);
    registerLocale('en-US', enUS);

    const localeCode: string | undefined = useLocale();
    const [locale, setLocale] = useState(enGB);
    var { getDatePickerFormat } = useDateUtils();

    useEffect(() => {
        if (!localeCode) {
            return;
        }

        switch (localeCode) {
            case "zh-CN":
                setLocale(zhCN);
                break;
            case "en-US":
                setLocale(enUS);
                break;
            default:
                setLocale(enGB);
                break;
        }
    }, [localeCode]);

    var { dateFormat, timeFormat } = getDatePickerFormat(dateOnly);
    var selectedDate = new Date(convertUTCToLocalTime(dateTimeUTC)?.toISO({includeOffset:false}) ?? "");
    const handleDateChange = (date: Date | null) => {
        // Convert JS Date in browser time, parse it as site local time, then convert to a UTC date
        let newDateTimeUTC = convertLocalTimeZoneToUTC(convertToDateTime(date));
        // Call provided callback method using UTC date
        handleDateSelect(newDateTimeUTC);
    }

    return (
        <DatePicker
            name={name}
            selected={selectedDate}
            onChange={handleDateChange}
            className={`form-control${isInvalid ? ' is-invalid' : ''}`}
            showIcon={showIcon}
            toggleCalendarOnIconClick
            isClearable={isClearable}
            required={required}
            locale={locale}
            dateFormat={dateFormat}
            timeFormat={timeFormat}
            showTimeSelect={!dateOnly}
            timeIntervals={1}
            inline={inline}
            calendarClassName={inline ? "position-absolute" : undefined}
            onClickOutside={onClickOutside}
            wrapperClassName={`${isInvalid ? ' is-invalid' : ''}`} // Adding the bootstrap classes to the wrapper allow control.Feedbacks to work as expected
        />
    );
};

export default DateTimePicker;